import {
  Alert,
  AttachmentField,
  Button,
  DateField,
  DateTimeField,
  Field,
  FormRow,
  FormSection,
  ReadonlyField,
  ReadonlyTextArea,
  SearchSelect,
  TextArea,
  type AttachmentItem,
} from '@evogard/ui';
import { useState, type ReactNode } from 'react';
import type { AnalysisForm, ClaimComment, ClaimDocument } from '@evogard/contract';
import {
  catalogChecklistValues,
  formatInstant,
  fromColumn,
  moneyInputText,
  type CatalogClaimType,
  type CatalogField,
  type CatalogFieldType,
  type CatalogFormState,
} from '@evogard/domain';
import { CheckboxGroup } from '../CheckboxGroup.js';
import { isFieldRequired, type Desk } from './desk.js';
import { claimFileUrl } from './useDesk.js';

/**
 * O que o formulário pergunta ao departamento: só o catálogo. A Análise e o
 * Reparo passam o `Desk` inteiro; Periféricos (08/10/2026), que não é
 * sinistro, passa o catálogo dele.
 */
export type PhaseFormDesk = Pick<
  Desk,
  'shownFields' | 'visibleFields' | 'phaseOf' | 'isDerived'
>;

/**
 * O formulário salvo, como este componente o lê. As duas listas que só o
 * sinistro tem — a oficina e os campos travados de uma fase corrigida —
 * faltam no de Periféricos, e faltar é o mesmo que vazio.
 */
export type PhaseFormData = Pick<
  AnalysisForm,
  'editable' | 'values' | 'counts' | 'comments' | 'files' | 'assignees'
> &
  Partial<Pick<AnalysisForm, 'lockedFields' | 'workshops' | 'suppliers'>>;

/** A âncora de um campo — é para ela que a lista de obrigatórios leva. */
export const fieldAnchor = (key: string) => `campo-fase-${key}`;

/** "2350.75" (o formato da API) → "2.350,75", como se digita no Brasil. */
const moneyText = (value: string) =>
  /^\d+\.\d{2}$/.test(value) ? moneyInputText(fromColumn(value)!) : value;

const subjectOf = (field: CatalogField) => field.binding.subject;

/** O que pede a linha inteira mesmo no formulário compacto: texto longo, lista de marcar, anexo e conexão. */
const FULL_WIDTH: ReadonlySet<CatalogFieldType> = new Set([
  'long_text',
  'checklist_vertical',
  'attachment',
  'connector',
]);

/**
 * As fileiras de uma seção. No compacto, dois campos curtos seguidos dividem
 * a fileira, e o que pede a linha inteira (`wide`) a tem sozinho — o curto
 * que sobra antes dele fica com a metade da esquerda. Fora do compacto, um
 * campo por fileira, como sempre foi.
 */
function rowsOf(
  fields: readonly CatalogField[],
  compact: boolean,
  wide: (field: CatalogField) => boolean,
): CatalogField[][] {
  if (!compact) return fields.map((f) => [f]);
  const rows: CatalogField[][] = [];
  let half: CatalogField | null = null;
  for (const field of fields) {
    if (wide(field)) {
      if (half) rows.push([half]);
      half = null;
      rows.push([field]);
    } else if (half) {
      rows.push([half, field]);
      half = null;
    } else {
      half = field;
    }
  }
  if (half) rows.push([half]);
  return rows;
}

/**
 * O formulário de uma fase — da Análise ou do Reparo —, desenhado pelo
 * catálogo do departamento (`@evogard/domain`), o mesmo que a API usa para
 * gravar e para recusar avançar. Nenhum campo é escrito à mão aqui.
 *
 * - **O valor mostrado é o rascunho por cima do salvo**: o salvo é o último
 *   NAQUELA fase (decisão do dono em 05/10/2026), e o rascunho é o que a
 *   pessoa mudou e ainda não gravou.
 * - **O campo que depende de outra resposta só aparece quando ela vale**
 *   (`showWhen`). A condição é lida do rascunho.
 * - **O calculado aparece e não se digita** — e o que o Reparo lê de outra
 *   etapa (o envio do orçamento, a chegada das peças) também.
 * - **A observação do Reparo é comentário** (07/10/2026): o campo escreve um
 *   comentário novo na Atividade, e o que a etapa já tem aparece contado.
 * - **Anexo sobe na hora**, e tirar o arquiva.
 * - **A etapa que junta duas colunas do pipe tem duas seções** (a Regulagem
 *   dentro da Orçamentação, a Chegada dentro da Compra de Peças).
 * - **O que ficou de fora por ora aparece listado e não trava** — e o campo
 *   ainda sem destino trava, como sempre.
 * - **Compacto, os campos curtos andam em pares** (`compact`, a Análise desde
 *   16/10/2026, o Reparo desde 20/10/2026 e a Indenização desde 22/10/2026):
 *   data, valor, lista de escolher e texto curto dividem a fileira; o resto tem
 *   a linha inteira.
 */
export function PhaseForm({
  desk,
  claimId,
  claimType,
  claimEvent = null,
  phase,
  form,
  draft,
  state,
  marked,
  onChange,
  onAttach,
  onArchive,
  uploading,
  readOnlyNotice = true,
  fileUrl,
  renderContacts,
  top,
  compact = false,
  fieldAction,
  onAddComment,
}: {
  desk: PhaseFormDesk;
  claimId: number;
  claimType: CatalogClaimType;
  /** O tipo do evento (`claims.claim_type`), que a Indenização pergunta — a procuração só no furto ou roubo. */
  claimEvent?: string | null;
  phase: string;
  form: PhaseFormData;
  draft: Readonly<Record<string, string | null>>;
  /** O salvo com o rascunho por cima, e os arquivos contados — o que as condições leem. */
  state: CatalogFormState;
  /** Os obrigatórios que a pessoa tentou pular — ficam em alerta. */
  marked: ReadonlySet<string>;
  onChange: (key: string, value: string | null) => void;
  onAttach: (key: string, files: File[], typesMime: string[]) => void;
  onArchive: (key: string, documentId: number) => void;
  /** O campo que está subindo arquivo agora. */
  uploading: string | null;
  /** O aviso de "Só leitura" — quem já diz por que a fase não se edita o dispensa. */
  readOnlyNotice?: boolean;
  /** Abre um arquivo gravado. Sem ela, o arquivo é do sinistro `claimId`. */
  fileUrl?: (documentId: number) => Promise<string>;
  /**
   * As tentativas de contato no lugar do número "Tentativas de contato", no
   * topo do formulário (15/10/2026): o botão que abre o registro e as
   * tentativas da fase. Quem tem mesa de sinistro a passa; Periféricos não
   * tem o campo.
   */
  renderContacts?: (required: boolean, error: string | null) => ReactNode;
  /**
   * Um bloco no alto do formulário, que não é campo do catálogo — os débitos
   * do Pagamento de Débito (29/10/2026), com o botão "Lançar despesa" que é o
   * mesmo da aba Despesas. Quem monta a mesa decide quando ele aparece.
   */
  top?: ReactNode;
  /** Os campos curtos lado a lado, em pares — a Análise (16/10/2026), o Reparo (20/10/2026) e a Indenização (22/10/2026). */
  compact?: boolean;
  /**
   * Uma ação embaixo de um campo, pelo nome dele — o "Atualizar link" do link
   * da CAF (16/10/2026). Quem monta a mesa decide; o formulário só desenha.
   */
  fieldAction?: (key: string) => ReactNode;
  /**
   * Grava já o comentário de um campo de observação (20/10/2026) — o botão
   * "Adicionar comentário" embaixo dele. Quem monta a mesa salva só aquele
   * campo e tira o texto do rascunho. Sem ela, o texto entra com o resto do
   * formulário, ao salvar ou avançar.
   */
  onAddComment?: (key: string, body: string) => Promise<unknown>;
}) {
  const context = { claimType, claimEvent };
  const fields = desk.shownFields(phase, state, context);
  const writable = fields.filter((f) => f.key);
  const visible = desk.visibleFields(phase);
  const unmapped = visible.filter((f) => subjectOf(f) === 'unmapped');
  const deferred = fields.filter((f) => subjectOf(f) === 'deferred');
  const label = desk.phaseOf(phase)?.label ?? phase;
  const readOnly = !form.editable;
  const locked = new Set(form.lockedFields ?? []);
  const urlOf = fileUrl ?? ((documentId: number) => claimFileUrl(claimId, documentId));
  const lockedShown = fields.filter((f) => f.key && locked.has(f.key));

  if (visible.length === 0) {
    /* A fase geral sem saída (Concluídos, por ora) não tem destino a
       escolher — mandar escolher contradizia a coluna da direita. */
    const hasMoves = (desk.phaseOf(phase)?.moves.length ?? 0) > 0;
    return (
      <p className="text-sm text-texto-suave">
        {label} não tem formulário.
        {hasMoves ? ' É só escolher o destino à direita.' : ''}
      </p>
    );
  }

  const valueOf = (key: string) => (key in draft ? draft[key] : form.values[key]) ?? '';
  const errorOf = (field: CatalogField) =>
    field.key && marked.has(field.key) ? 'Obrigatório para avançar.' : null;

  /* As seções, na ordem em que aparecem no catálogo: a da própria etapa
     primeiro, e a de cada coluna do pipe que ela juntou depois. */
  /* As tentativas de contato são um botão só (15/10/2026): na seção delas,
     quando o catálogo dá uma, e no topo quando não dá. */
  const contactField = renderContacts
    ? writable.find((f) => subjectOf(f) === 'contact_count')
    : undefined;
  const contactOnTop = contactField !== undefined && !contactField.section;
  const sections: { title: string; fields: CatalogField[] }[] = [];
  for (const field of writable) {
    if (field === contactField && contactOnTop) continue;
    const title = field.section ?? `O que ${label} pede`;
    const section = sections.find((s) => s.title === title);
    if (section) section.fields.push(field);
    else sections.push({ title, fields: [field] });
  }

  /* O registro das tentativas e o comentário também pedem a linha inteira. */
  const wide = (field: CatalogField) =>
    field === contactField ||
    FULL_WIDTH.has(field.type) ||
    subjectOf(field) === 'comment';
  const contactBlock = (field: CatalogField) => (
    <div key={field.key} id={fieldAnchor(field.key!)} className="scroll-mt-4">
      {renderContacts!(isFieldRequired(field, state, context), errorOf(field))}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {readOnly && readOnlyNotice && (
        <Alert tone="informacao">Só leitura: {label} não se edita daqui.</Alert>
      )}
      {!readOnly && lockedShown.length > 0 && (
        <p className="text-xs text-texto-suave">
          {lockedShown.map((f) => f.label).join(', ')}{' '}
          {lockedShown.length === 1 ? 'é' : 'são'} da fase onde o sinistro está, e não se
          {lockedShown.length === 1 ? ' corrige' : ' corrigem'} aqui.
        </p>
      )}
      {contactOnTop && renderContacts && contactBlock(contactField)}
      {top}
      {sections.map((section) => (
        <FormSection key={section.title} title={section.title}>
          {rowsOf(section.fields, compact, wide).map((row) => (
            <FormRow
              key={row[0]!.key}
              cols={row.length === 2 ? [6, 6] : compact && !wide(row[0]!) ? [6] : [12]}
            >
              {row.map((field) => {
                const key = field.key!;
                return field === contactField ? (
                  contactBlock(field)
                ) : (
                  <div key={key} id={fieldAnchor(key)} className="scroll-mt-4">
                    <PhaseField
                      desk={desk}
                      fileUrl={urlOf}
                      field={field}
                      form={form}
                      value={valueOf(key)}
                      required={isFieldRequired(field, state, context)}
                      readOnly={readOnly || locked.has(key)}
                      error={errorOf(field)}
                      onChange={(v) => onChange(key, v)}
                      onAttach={(files, types) => onAttach(key, files, types)}
                      onArchive={(documentId) => onArchive(key, documentId)}
                      uploading={uploading === key}
                      onAddComment={
                        onAddComment && !readOnly
                          ? (body) => onAddComment(key, body)
                          : undefined
                      }
                    />
                    {fieldAction?.(key)}
                  </div>
                );
              })}
            </FormRow>
          ))}
        </FormSection>
      ))}
      {deferred.length > 0 && (
        <Alert
          tone="informacao"
          title={`${deferred.length} ${deferred.length === 1 ? 'campo ainda não grava' : 'campos ainda não gravam'}`}
        >
          <p>
            Ficaram de fora por ora, por decisão, e não travam o avanço. A tela não os
            preenche de mentira:
          </p>
          <ul className="mt-1 list-disc pl-5">
            {deferred.map((f) => (
              <li key={f.label}>{f.label}</li>
            ))}
          </ul>
        </Alert>
      )}
      {unmapped.length > 0 && (
        <Alert
          tone={unmapped.some((f) => f.required) ? 'alerta' : 'informacao'}
          title={`${unmapped.length} ${unmapped.length === 1 ? 'campo sem destino' : 'campos sem destino'}`}
        >
          <p>
            O destino destes campos no banco ainda não foi decidido, e a tela não os
            preenche de mentira:
          </p>
          <ul className="mt-1 list-disc pl-5">
            {unmapped.map((f) => (
              <li key={f.label}>
                {f.label}
                {f.required && ' (obrigatório)'}
              </li>
            ))}
          </ul>
          {unmapped.some((f) => f.required) && (
            <p className="mt-1">
              Por ter obrigatório sem destino, {label} não avança por enquanto. Voltar
              continua livre.
            </p>
          )}
        </Alert>
      )}
    </div>
  );
}

/** Um campo do catálogo, desenhado pelo tipo dele. */
function PhaseField({
  desk,
  fileUrl,
  field,
  form,
  value,
  required,
  readOnly,
  error,
  onChange,
  onAttach,
  onArchive,
  uploading,
  onAddComment,
}: {
  desk: PhaseFormDesk;
  fileUrl: (documentId: number) => Promise<string>;
  field: CatalogField;
  form: PhaseFormData;
  value: string;
  required: boolean;
  readOnly: boolean;
  error: string | null;
  onChange: (value: string | null) => void;
  onAttach: (files: File[], typesMime: string[]) => void;
  onArchive: (documentId: number) => void;
  uploading: boolean;
  onAddComment?: (body: string) => Promise<unknown>;
}) {
  const key = field.key!;
  const set = (v: string) => onChange(v === '' ? null : v);

  /* O calculado aparece e não se digita — `ReadonlyField` (15/10/2026). */
  if (desk.isDerived(field)) {
    const shown =
      field.type === 'number'
        ? value || '0'
        : field.type === 'currency' && value
          ? `R$ ${moneyText(value)}`
          : value
            ? displayValue(field, value)
            : '';
    if (field.type === 'long_text') {
      return (
        <ReadonlyTextArea
          label={field.label}
          value={shown}
          help={field.help}
          required={required}
          error={error}
        />
      );
    }
    return (
      <ReadonlyField
        label={field.label}
        value={shown}
        help={field.help}
        required={required}
        error={error}
        icon={
          field.type === 'due_date' || field.type === 'datetime' || field.type === 'date'
            ? 'relogio'
            : undefined
        }
      />
    );
  }

  /* A observação é comentário (20/10/2026): o campo registra, e os já
     registrados por ele ficam listados embaixo. */
  if (subjectOf(field) === 'comment') {
    return (
      <FieldComments
        field={field}
        comments={form.comments?.[key] ?? []}
        value={value}
        required={required}
        readOnly={readOnly}
        error={error}
        onChange={set}
        onAdd={onAddComment}
      />
    );
  }

  switch (field.type) {
    case 'select':
    case 'radio_vertical':
    case 'assignee_select':
    case 'connector': {
      /* Qual cadastro o campo de empresa oferece (21/10/2026). */
      const supplier = field.companyTypes?.includes('fornecedor_pecas') ?? false;
      const companies = supplier ? (form.suppliers ?? []) : (form.workshops ?? []);
      return (
        <SearchSelect
          label={field.label}
          required={required}
          options={
            field.type === 'assignee_select'
              ? form.assignees.map((p) => ({ value: p.id, label: p.name }))
              : field.type === 'connector'
                ? companies.map((w) => ({ value: w.id, label: w.name }))
                : (field.options ?? [])
          }
          value={value}
          onChange={set}
          clearable
          sortable={field.type === 'connector' || field.type === 'assignee_select'}
          disabled={readOnly}
          error={error}
          help={
            field.type === 'connector' && companies.length === 0
              ? `Nenhum${supplier ? ' fornecedor de peças nem oficina cadastrados' : 'a oficina cadastrada'} nesta base. Cadastre em Plataforma › Cadastros › Prestadores.`
              : field.help
          }
        />
      );
    }
    case 'checklist_vertical':
      return (
        <CheckboxGroup
          label={field.label}
          required={required}
          options={field.options ?? []}
          selected={catalogChecklistValues(value)}
          onChange={(picked) => onChange(picked.length > 0 ? picked.join(',') : null)}
          disabled={readOnly}
          error={error}
          help={field.help}
        />
      );
    case 'date':
      return (
        <DateField
          label={field.label}
          layer="modal"
          required={required}
          value={value}
          onChange={set}
          disabled={readOnly}
          error={error}
          help={field.help}
        />
      );
    case 'datetime':
    case 'due_date':
      return field.dateOnly ? (
        <DateField
          label={field.label}
          layer="modal"
          required={required}
          value={value}
          onChange={set}
          disabled={readOnly}
          error={error}
          help="A data, sem a hora: é o que o cadastro do veículo recuperado guarda."
        />
      ) : (
        <DateTimeField
          label={field.label}
          layer="modal"
          required={required}
          value={value}
          onChange={set}
          disabled={readOnly}
          error={error}
          help={field.help}
        />
      );
    case 'currency':
      return (
        <Field
          label={field.label}
          prefix="R$"
          mask="money"
          required={required}
          value={value}
          onChange={(e) => set(e.target.value)}
          disabled={readOnly}
          error={error}
          help={field.help}
        />
      );
    case 'long_text':
      return (
        <TextArea
          label={field.label}
          required={required}
          value={value}
          onChange={(e) => set(e.target.value)}
          maxLength={4000}
          disabled={readOnly}
          error={error}
          help={field.help}
        />
      );
    case 'short_text':
      return (
        <Field
          label={field.label}
          required={required}
          value={value}
          onChange={(e) => set(e.target.value)}
          maxLength={4000}
          disabled={readOnly}
          error={error}
          help={field.help}
        />
      );
    case 'attachment':
      return (
        <div className="flex flex-col gap-1.5">
          <AttachmentField
            label={
              <>
                {field.label}
                {required && <span className="text-erro"> *</span>}
              </>
            }
            items={(form.files[key] ?? []).map((d) => fileItem(d, fileUrl))}
            onAdd={onAttach}
            onRemove={(docKey) => onArchive(Number(docKey))}
            disabled={readOnly}
            busy={uploading}
          />
          {error && <p className="text-rotulo text-erro">{error}</p>}
        </div>
      );
    default:
      return null;
  }
}

/** O calculado, como a casa mostra: "06/10/2026, 14:30" ou "06/10/2026". */
function displayValue(field: CatalogField, value: string): string {
  if (field.type !== 'datetime' && field.type !== 'due_date' && field.type !== 'date') {
    return value;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}:\d{2}))?$/.exec(value);
  return m ? `${m[3]}/${m[2]}/${m[1]}${m[4] ? `, ${m[4]}` : ''}` : value;
}

/** Um arquivo gravado, no formato da grade da casa — assinado no clique. */
function fileItem(
  d: ClaimDocument,
  fileUrl: (documentId: number) => Promise<string>,
): AttachmentItem {
  return {
    key: String(d.id),
    nome: d.fileName,
    typeMime: d.mimeType,
    sizeBytes: d.sizeBytes,
    detail: [d.createdByName, formatInstant(d.createdAt)].filter(Boolean).join(' · '),
    resolveUrl: () => fileUrl(d.id),
  };
}

/**
 * O campo de observação (20/10/2026): comentário e observação são a mesma
 * coisa, e o rótulo do campo é o porquê. O texto se escreve aqui e entra pelo
 * botão "Adicionar comentário" — ou com o resto do formulário, ao salvar ou
 * avançar —, sempre como um comentário novo. Embaixo, os já feitos por este
 * campo nesta fase, como as tentativas de contato; a Atividade mostra os
 * mesmos, com o rótulo junto.
 */
function FieldComments({
  field,
  comments,
  value,
  required,
  readOnly,
  error,
  onChange,
  onAdd,
}: {
  field: CatalogField;
  comments: readonly ClaimComment[];
  value: string;
  required: boolean;
  readOnly: boolean;
  error: string | null;
  onChange: (value: string) => void;
  onAdd?: (body: string) => Promise<unknown>;
}) {
  const [adding, setAdding] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const text = value.trim();
  const add = async () => {
    setAdding(true);
    setFailure(null);
    try {
      await onAdd!(text);
    } catch (e) {
      setFailure((e as Error).message);
    } finally {
      setAdding(false);
    }
  };
  const count = comments.length;

  return (
    <div className="flex flex-col gap-2">
      <TextArea
        label={field.label}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={4000}
        rows={2}
        disabled={readOnly}
        error={error ?? failure}
        placeholder="Escreva o comentário."
        help={
          field.help ??
          (count === 0
            ? 'Nenhum comentário registrado aqui nesta etapa.'
            : `${count === 1 ? 'Um comentário registrado' : `${count} comentários registrados`} aqui nesta etapa.`)
        }
      />
      {onAdd && (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="secundario"
            disabled={text === ''}
            loading={adding}
            onClick={() => void add()}
          >
            Adicionar comentário
          </Button>
        </div>
      )}
      {count > 0 && (
        <ul className="flex flex-col gap-1.5">
          {comments.map((c) => (
            <li
              key={c.id}
              className="flex flex-col gap-0.5 rounded-borda border border-borda-suave px-3 py-2 text-sm"
            >
              <p className="text-xs text-texto-tenue">
                <span className="font-semibold text-texto-suave">
                  {c.authorName ?? 'Sem nome'}
                </span>
                {' · '}
                {formatInstant(c.createdAt)}
                {c.editedAt && ' · editado'}
              </p>
              <p className="whitespace-pre-line text-texto">{c.body}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
